import React, { useCallback } from 'react';

import { Switch, SwitchProps, Tooltip } from '@mui/material';
import { MAIN_THEME } from '@styles';

import { IFormFieldInputBaseProps } from '.';

import clsx from 'clsx';
import { makeStyles } from 'tss-react/mui';

interface IEnhancedSwitchProps
  extends IFormFieldInputBaseProps,
    Omit<SwitchProps, 'onChange'> {
  title: string;
  name: string;
  error?: string;
  value?: boolean;
  information?: string;
  informationIconUrl?: string;
  onChange: (name: string, value: boolean) => void;
}

const useStyles = makeStyles()(() => ({
  labelWrapper: {
    display: 'block',
    textAlign: 'left',
    fontSize: MAIN_THEME.fontSize.sm,
    lineHeight: '15px',
    color: MAIN_THEME.color.black,
    fontFamily: MAIN_THEME.font.regular
  },
  label: {
    textAlign: 'left',
    minWidth: 'fit-content'
  },
  switch: {
    margin: '0 0 0 20px',
    '& .MuiSwitch-input': {
      width: '100%',
      left: 'unset'
    },
    '& .MuiSwitch-root': {
      color: MAIN_THEME.color.black
    },
    '& .Mui-checked': {
      color: `${MAIN_THEME.color.black} !important`,
      '&:hover': {
        backgroundColor: MAIN_THEME.color.black47
      },
      '&  + .MuiSwitch-track': {
        backgroundColor: `${MAIN_THEME.color.black} !important`
      }
    }
  },
  error: {
    textAlign: 'right',
    fontSize: MAIN_THEME.fontSize.xs,
    minHeight: '15px',
    display: 'block',
    color: MAIN_THEME.color.red
  },
  iconContainer: {
    minWidth: '20px',
    height: '20px',
    margin: '0 0 0 10px',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '80% 80%'
  },
  containerWithIcon: {
    justifyContent: 'space-between'
  },
  container: {
    display: 'flex'
  },
  labelContainer: {
    display: 'flex',
    alignItems: 'center'
  }
}));

const SwitchFormField: React.FC<IEnhancedSwitchProps> = ({
  title,
  name,
  value,
  information,
  informationIconUrl,
  error,
  onBlur,
  onChange,
  disabled,
  className
}) => {
  const { classes } = useStyles();

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      onChange(event.target.name, checked);
    },
    [onChange]
  );

  return (
    <label htmlFor={name} className={clsx(classes.labelWrapper, className)}>
      <div
        className={clsx(classes.container, {
          [classes.containerWithIcon]: information && informationIconUrl
        })}
      >
        <span className={classes.labelContainer}>
          <span className={classes.label}>{title}</span>
          {information && informationIconUrl && (
            <Tooltip title={information}>
              <figure
                className={classes.iconContainer}
                style={{ backgroundImage: `url(${informationIconUrl})` }}
              />
            </Tooltip>
          )}
        </span>
        <Tooltip title={information}>
          <Switch
            id={name}
            name={name}
            className={classes.switch}
            onChange={handleChange}
            aria-invalid={!!error}
            aria-describedby={`errorMsg-${name}`}
            checked={value}
            value={value}
            onBlur={onBlur}
            disabled={disabled}
          />
        </Tooltip>
      </div>
      <span id={`errorMsg-${name}`} className={classes.error}>
        {error}
      </span>
    </label>
  );
};

export default SwitchFormField;
